import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import SEO from '@/components/SEO';
import axios from 'axios';
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Truck, Award, Wrench } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  address: 'Str. Henri Coandă 33A, Brașov, România',
  phone: '0761 961 789',
  email: 'vanzari@shagunsurfaces.ro',
  schedule: 'Luni - Vineri: 09:00 - 18:00, Sâmbătă: 09:00 - 14:00',
  whatsapp: '40761961789',
  instagram: 'https://www.instagram.com/shagunsurfaces/',
  facebook: 'https://www.facebook.com/profile.php?id=61574700722525',
};

const ContactPage = () => {
  const [cms, setCms] = useState(DEFAULTS);

  useEffect(() => {
    axios.get(`${API_URL}/api/settings`).then(r => {
      if (r.data && Object.keys(r.data).length > 0) setCms(prev => ({ ...prev, ...r.data }));
    }).catch(() => {});
  }, []);

  return (
    <Layout>
      <SEO
        title="Contact - Shagun Surfaces Brașov"
        description={`Contactează Shagun Surfaces: ${cms.phone}. Showroom: ${cms.address}. Consultanță gratuită, livrare în toată România.`}
        canonicalUrl="https://shagunsurfaces.ro/contact"
      />
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#050505]" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
            Contact
          </h1>
          <p className="text-[#A3A3A3] text-lg max-w-2xl">
            Suntem aici să te ajutăm. Contactează-ne pentru întrebări, oferte sau programare consultanță.
          </p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 bg-[#0a0a0a]" data-testid="contact-info">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left - Info */}
            <div>
              <h2 className="font-serif text-3xl text-white mb-8">Informații Contact</h2>
              
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4 p-6 bg-[#121212] border border-white/5">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#D4AF37]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-2">Adresă Showroom</h3>
                    <p className="text-[#A3A3A3]">{cms.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-[#121212] border border-white/5">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#D4AF37]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-2">Telefon / WhatsApp</h3>
                    <a href={`tel:+${cms.whatsapp || '40761961789'}`} className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors">
                      {cms.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-[#121212] border border-white/5">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#D4AF37]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-2">Email</h3>
                    <a href={`mailto:${cms.email}`} className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors">
                      {cms.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-[#121212] border border-white/5">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="text-[#D4AF37]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-2">Program</h3>
                    <p className="text-[#A3A3A3] whitespace-pre-line">{cms.schedule}</p>
                  </div>
                </div>
              </div>

              {/* Social */}
              <div>
                <h3 className="text-white font-medium mb-4">Urmărește-ne</h3>
                <div className="flex gap-4">
                  {cms.instagram && (
                    <a href={cms.instagram} target="_blank" rel="noopener noreferrer"
                      className="w-12 h-12 bg-[#121212] border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                      data-testid="contact-instagram">
                      <Instagram size={22} />
                    </a>
                  )}
                  {cms.facebook && (
                    <a href={cms.facebook} target="_blank" rel="noopener noreferrer"
                      className="w-12 h-12 bg-[#121212] border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                      data-testid="contact-facebook">
                      <Facebook size={22} />
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Right - WhatsApp CTA */}
            <div>
              <div className="bg-[#121212] border border-white/10 p-8 mb-8">
                <h2 className="font-serif text-2xl text-white mb-4">Contactează-ne Rapid</h2>
                <p className="text-[#A3A3A3] mb-6">
                  Cel mai rapid mod de a ne contacta este prin WhatsApp. 
                  Răspundem în câteva minute în timpul programului de lucru.
                </p>
                <a href={`https://wa.me/${cms.whatsapp || '40761961789'}`} target="_blank" rel="noopener noreferrer"
                  className="w-full bg-[#25D366] text-white font-semibold py-4 px-6 flex items-center justify-center gap-3 hover:bg-[#20bd5a] transition-colors"
                  data-testid="contact-whatsapp-btn">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Scrie-ne pe WhatsApp
                </a>
              </div>

              {/* Services */}
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-8">
                <h3 className="font-serif text-xl text-white mb-6">Ce Oferim</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Wrench className="text-[#D4AF37]" size={20} />
                    <span className="text-[#A3A3A3]">Consultanță profesională gratuită</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="text-[#D4AF37]" size={20} />
                    <span className="text-[#A3A3A3]">Deplasare la fața locului pentru măsurători</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Truck className="text-[#D4AF37]" size={20} />
                    <span className="text-[#A3A3A3]">Livrare în toată România</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Award className="text-[#D4AF37]" size={20} />
                    <span className="text-[#A3A3A3]">Produse premium certificate</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="h-96 bg-[#121212]" data-testid="contact-map">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2787.5!2d25.5869!3d45.6557!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDXCsDM5JzIwLjUiTiAyNcKwMzUnMTIuOCJF!5e0!3m2!1sen!2sro!4v1234567890"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Locație Shagun Surfaces"
        />
      </section>
    </Layout>
  );
};

export default ContactPage;
